"use client";
import React, { useEffect, useState } from 'react';
import { showSuccess, showError } from '@/utils/toast';

// Share row used in the glossary detail hero and sidebar.
export default function GlossaryShare() {
  const [pageUrl, setPageUrl] = useState('');
  const [pageTitle, setPageTitle] = useState('');

  useEffect(() => {
    setPageUrl(window.location.href);
    setPageTitle(document.title);
  }, []);

  const encodedUrl = encodeURIComponent(pageUrl);
  const encodedTitle = encodeURIComponent(pageTitle);
  const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
  const twitterShareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;

  const handleCopyLink = async () => {
    const url = pageUrl || (typeof window !== 'undefined' ? window.location.href : '');
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      showSuccess('Link copied to clipboard');
    } catch (error) {
      showError('Failed to copy link');
    }
  };

  return (
    <div className="share-icons d-flex gap-3">
      <a
        href="#"
        className="share-icon"
        role="button"
        aria-label="Copy link"
        title="Copy link"
        style={{ cursor: 'pointer' }}
        onClick={(e) => { e.preventDefault(); handleCopyLink(); }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
          <rect width="24" height="24" fill="none" />
          <path d="M8.22172 23.4288C8.68559 23.8934 9.23669 24.2617 9.84334 24.5126C10.45 24.7635 11.1002 24.892 11.7567 24.8908C12.4133 24.892 13.0637 24.7634 13.6705 24.5125C14.2774 24.2616 14.8286 23.8934 15.2927 23.4288L18.1207 20.5998L16.7067 19.1858L13.8787 22.0148C13.3152 22.5759 12.5524 22.8908 11.7572 22.8908C10.962 22.8908 10.1992 22.5759 9.63572 22.0148C9.07422 21.4516 8.75892 20.6887 8.75892 19.8933C8.75892 19.098 9.07422 18.3351 9.63572 17.7718L12.4647 14.9438L11.0507 13.5298L8.22172 16.3578C7.28552 17.2963 6.75977 18.5678 6.75977 19.8933C6.75977 21.2189 7.28552 22.4904 8.22172 23.4288ZM23.7777 14.9438C24.7134 14.0051 25.2388 12.7338 25.2388 11.4083C25.2388 10.0829 24.7134 8.81156 23.7777 7.87284C22.8393 6.93665 21.5678 6.41089 20.2422 6.41089C18.9166 6.41089 17.6452 6.93665 16.7067 7.87284L13.8787 10.7018L15.2927 12.1158L18.1207 9.28684C18.6842 8.72583 19.447 8.41087 20.2422 8.41087C21.0374 8.41087 21.8002 8.72583 22.3637 9.28684C22.9252 9.85011 23.2405 10.613 23.2405 11.4083C23.2405 12.2037 22.9252 12.9666 22.3637 13.5298L19.5347 16.3578L20.9487 17.7718L23.7777 14.9438Z" fill="#0C0C0C" />
          <path d="M12.4638 20.6009L11.0488 19.1869L19.5358 10.7009L20.9498 12.1159L12.4638 20.6009Z" fill="#0C0C0C" />
        </svg>
      </a>
      <a href={linkedinShareUrl} className="share-icon" target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" title="Share on LinkedIn">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" fill="#0C0C0C" />
        </svg>
      </a>
      <a href={twitterShareUrl} className="share-icon" target="_blank" rel="noopener noreferrer" aria-label="Share on X" title="Share on X">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="#0C0C0C" />
        </svg>
      </a>
      <a href={facebookShareUrl} className="share-icon" target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" title="Share on Facebook">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#0C0C0C" />
        </svg>
      </a>
    </div>
  );
}
